import { alpha, createTheme } from "@mui/material/styles";

const fontFamily = [
  "Inter",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  "sans-serif",
].join(",");

const ink = {
  900: "#0F172A",
  700: "#334155",
  500: "#64748B",
  400: "#94A3B8",
  300: "#CBD5E1",
  200: "#E2E8F0",
  100: "#F1F5F9",
  50: "#F8FAFC",
};

export const brand = {
  gradient: "linear-gradient(135deg, #6157E0 0%, #8B5CF6 55%, #C084FC 100%)",
  softGradient:
    "radial-gradient(1200px 500px at 10% -10%, rgba(97,87,224,0.10), transparent 60%), radial-gradient(900px 400px at 100% 0%, rgba(236,72,153,0.07), transparent 55%)",
};

const theme = createTheme({
  cssVariables: false,
  palette: {
    mode: "light",
    primary: { main: "#6157E0", light: "#8B84F0", dark: "#4A41C4" },
    secondary: { main: "#EC4899", light: "#F472B6", dark: "#BE2A78" },
    success: { main: "#10B981", dark: "#059669" },
    info: { main: "#0EA5E9", light: "#38BDF8", dark: "#0284C7" },
    warning: { main: "#F59E0B", dark: "#D97706" },
    error: { main: "#EF4444", dark: "#DC2626" },
    text: { primary: ink[900], secondary: ink[500] },
    divider: ink[200],
    background: { default: ink[50], paper: "#FFFFFF" },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily,
    h3: { fontWeight: 800, letterSpacing: "-0.03em" },
    h4: { fontWeight: 700, letterSpacing: "-0.02em", fontSize: "1.75rem" },
    h5: { fontWeight: 700, letterSpacing: "-0.01em" },
    h6: { fontWeight: 700, letterSpacing: "-0.01em" },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: ink[50],
          backgroundImage: brand.softGradient,
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
        },
        "::selection": { background: alpha("#6157E0", 0.2) },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 16,
          transition: "all .2s ease",
        },
        sizeLarge: { paddingBlock: 11, fontSize: "0.95rem" },
        containedPrimary: {
          backgroundImage: "linear-gradient(135deg, #6157E0 0%, #7C6CF0 100%)",
          boxShadow: "0 6px 16px -6px rgba(97,87,224,0.6)",
          "&:hover": {
            boxShadow: "0 10px 22px -8px rgba(97,87,224,0.75)",
            transform: "translateY(-1px)",
          },
        },
        outlined: { borderColor: ink[300], color: ink[700] },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${ink[200]}`,
          borderRadius: 16,
          boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)",
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none" },
        rounded: { borderRadius: 16 },
        outlined: { borderColor: ink[200] },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha("#FFFFFF", 0.8),
          backdropFilter: "saturate(180%) blur(14px)",
          color: ink[900],
          boxShadow: "none",
          borderBottom: `1px solid ${ink[200]}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: "0 30px 80px -20px rgba(15,23,42,0.35)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontWeight: 700, fontSize: "1.2rem", paddingTop: 22 } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: "12px 24px 20px", gap: 4 } },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined" },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#fff",
          "& fieldset": { borderColor: ink[300] },
          "&:hover fieldset": { borderColor: ink[500] },
        },
      },
    },
    // Listas suspensas com fundo cinza claro para se ver onde terminam
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: ink[200],
          border: `1px solid ${ink[400]}`,
          boxShadow: "0 12px 28px -12px rgba(15,23,42,0.25)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: ink[200],
          border: `1px solid ${ink[400]}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: { backgroundColor: ink[900], borderRadius: 8, fontSize: 12 },
        arrow: { color: ink[900] },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${ink[100]}`, padding: "14px 16px" },
        head: {
          backgroundColor: ink[50],
          color: ink[500],
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          borderBottom: `1px solid ${ink[200]}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child td": { borderBottom: 0 },
          "&.MuiTableRow-hover:hover": { backgroundColor: alpha("#6157E0", 0.04) },
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 8, height: 6 } },
    },
  },
});

export default theme;
